import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
    Percent,
    DollarSign,
    Calendar,
    Save,
    Package,
    Tag,
    ChevronLeft
} from 'lucide-react';

const BulkDiscount = () => {
    const navigate = useNavigate();
    const [discountForm, setDiscountForm] = useState({
        type: 'percentage',
        value: '',
        startDate: '',
        endDate: '',
        category: '',
        applyToAll: false
    });

    // Updated categories to include Egg category
    const categories = [
        "Mutton - Fresh Cuts",
        "Mutton - Premium Cuts",
        "Chicken - Fresh Cuts",
        "Chicken - Special Cuts",
        "Egg -- Fresh Egg"
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const { data } = await axios.post(
                '/api/v1/admin/products/bulk-discount',
                discountForm,
                { withCredentials: true }
            );
            if (data.success) {
                toast.success('Bulk discount applied successfully');
                setDiscountForm({
                    type: 'percentage',
                    value: '',
                    startDate: '',
                    endDate: '',
                    category: '',
                    applyToAll: false
                });
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error applying bulk discount');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-3 sm:p-5">
            <div className="max-w-3xl mx-auto">
                {/* Header with title and back button */}
                <div className="flex justify-between items-center mb-4 sm:mb-6">
                    <h1 className="text-lg sm:text-xl font-bold text-gray-900 font-['Crimson_Text']">
                        Bulk Discount
                    </h1>
                    <button
                        onClick={() => navigate('/admin/dashboard')}
                        className="flex items-center gap-1 sm:gap-2 text-gray-600 hover:text-[#dc2626] transition-colors py-1.5 px-2.5 rounded-lg hover:bg-gray-100"
                    >
                        <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        <span className="text-xs sm:text-sm hidden xs:inline">Back to Dashboard</span>
                    </button>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
                    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                            {/* Discount Type */}
                            <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                    Discount Type
                                </label>
                                <select
                                    value={discountForm.type}
                                    onChange={(e) => setDiscountForm(prev => ({
                                        ...prev,
                                        type: e.target.value
                                    }))}
                                    className="block w-full pl-2.5 sm:pl-3 pr-8 sm:pr-10 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626]"
                                >
                                    <option value="percentage">Percentage Off</option>
                                    <option value="amount">Fixed Amount Off</option>
                                </select>
                            </div>
                            {/* Discount Value */}
                            <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                    {discountForm.type === 'percentage' ? 'Percentage' : 'Amount'}
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-2 sm:pl-3 flex items-center pointer-events-none">
                                        {discountForm.type === 'percentage' ? (
                                            <Percent className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                                        ) : (
                                            <DollarSign className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                                        )}
                                    </div>
                                    <input
                                        type="number"
                                        value={discountForm.value}
                                        onChange={(e) => setDiscountForm(prev => ({
                                            ...prev,
                                            value: e.target.value
                                        }))}
                                        className="block w-full pl-8 sm:pl-10 pr-2 sm:pr-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626]"
                                        placeholder={discountForm.type === 'percentage' ? 'Enter percentage' : 'Enter amount'}
                                        required
                                    />
                                </div>
                            </div>
                            {/* Date Range */}
                            <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                    Start Date
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-2 sm:pl-3 flex items-center pointer-events-none">
                                        <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                                    </div>
                                    <input
                                        type="date"
                                        value={discountForm.startDate}
                                        onChange={(e) => setDiscountForm(prev => ({
                                            ...prev,
                                            startDate: e.target.value
                                        }))}
                                        className="block w-full pl-8 sm:pl-10 pr-2 sm:pr-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626]"
                                        required
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                    End Date
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-2 sm:pl-3 flex items-center pointer-events-none">
                                        <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                                    </div>
                                    <input
                                        type="date"
                                        value={discountForm.endDate}
                                        onChange={(e) => setDiscountForm(prev => ({
                                            ...prev,
                                            endDate: e.target.value
                                        }))}
                                        className="block w-full pl-8 sm:pl-10 pr-2 sm:pr-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626]"
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                        {/* Category Selection */}
                        <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                Apply To
                            </label>
                            <div className="space-y-3 sm:space-y-4">
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        checked={discountForm.applyToAll}
                                        onChange={() => setDiscountForm(prev => ({
                                            ...prev,
                                            applyToAll: true,
                                            category: ''
                                        }))}
                                        className="text-[#dc2626] focus:ring-[#dc2626] h-3.5 w-3.5 sm:h-4 sm:w-4"
                                    />
                                    <span className="ml-2 text-xs sm:text-sm">All Products</span>
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        checked={!discountForm.applyToAll}
                                        onChange={() => setDiscountForm(prev => ({
                                            ...prev,
                                            applyToAll: false
                                        }))}
                                        className="text-[#dc2626] focus:ring-[#dc2626] h-3.5 w-3.5 sm:h-4 sm:w-4"
                                    />
                                    <span className="ml-2 text-xs sm:text-sm">Specific Category</span>
                                </label>
                                {!discountForm.applyToAll && (
                                    <select
                                        value={discountForm.category}
                                        onChange={(e) => setDiscountForm(prev => ({
                                            ...prev,
                                            category: e.target.value
                                        }))}
                                        className="block w-full pl-2.5 sm:pl-3 pr-8 sm:pr-10 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626]"
                                        required={!discountForm.applyToAll}
                                    >
                                        <option value="">Select Category</option>
                                        {categories.map(category => (
                                            <option key={category} value={category}>
                                                {category}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        </div>
                        {/* Submit Button */}
                        <div className="flex justify-end">
                            <button
                                type="submit"
                                className="px-4 sm:px-5 py-1.5 sm:py-2 bg-[#dc2626] text-white text-xs sm:text-sm rounded-lg hover:bg-[#dc2626]/90 transition-colors flex items-center gap-1.5 sm:gap-2"
                            >
                                <Save className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                Apply Bulk Discount
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default BulkDiscount;